import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip } from 'react-tooltip';
import { BarChart2, Eye, GitCompare, Globe, GlobeLock, Loader2, Trash2, WrenchIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../hooks/useConfirm';

function HelmCardSkeleton() {
  return (
    <Card className="flex flex-col">
      <CardContent className="flex flex-col gap-2 p-4 h-full">
        <Skeleton className="h-5 w-2/5 mb-0.5" />
        <Skeleton className="h-3 w-3/5" />
        <div className="flex gap-2 min-h-[20px]">
          <Skeleton className="h-5 w-16 rounded" />
          <Skeleton className="h-5 w-16 rounded" />
        </div>
        <div className="mt-auto flex gap-1.5 pt-1">
          <Skeleton className="h-7 flex-1" />
          <Skeleton className="h-7 flex-1" />
          <Skeleton className="h-7 w-8" />
        </div>
      </CardContent>
    </Card>
  );
}

function fmt(val, decimals = 1) {
  if (val == null) return null;
  return parseFloat(val).toFixed(decimals);
}

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function HelmList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = user?.is_superadmin;
  const [helms, setHelms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirm();

  const fetchHelms = () =>
    api.get('/helms').then(r => setHelms(r.data)).finally(() => setLoading(false));

  useEffect(() => { fetchHelms(); }, []);

  // Saved walk-forward run configs — the entry point to comparing the same
  // config's runs across helms.
  const [wfConfigs, setWfConfigs] = useState([]);
  useEffect(() => {
    api.get('/wf-configs').then(r => setWfConfigs(r.data)).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const res = await api.post('/helms', { name: newName.trim() });
      setNewName('');
      navigate(`/helms/${res.data.id}`, { state: { is_owner: true, is_global: false } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create helm');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!(await confirm(`This will permanently delete "${name}" and all its data.`, {
      title: 'Delete helm?', confirmText: 'Delete',
    }))) return;
    await api.delete(`/helms/${id}`);
    setHelms(prev => prev.filter(h => h.id !== id));
  };

  const handleToggleGlobal = async (id) => {
    try {
      const res = await api.post(`/admin/helms/${id}/toggle-global`);
      setHelms(prev => prev.map(h => h.id === id ? { ...h, is_global: res.data.is_global } : h));
    } catch {
      // silent — server returns 403 if not superadmin
    }
  };

  return (
    <AppLayout>
      <main className="md:p-6 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-foreground whitespace-nowrap">Trading Helms</h2>
          <form onSubmit={handleCreate} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full md:w-auto">
            <Input
              type="text"
              placeholder="New helm name…"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              required
              className="h-9 sm:h-8 text-sm flex-1 sm:flex-none sm:w-28 lg:w-40"
            />
            <Button type="submit" size="sm" disabled={creating} className="h-9 sm:h-8 w-full sm:w-auto shrink-0">
              {creating ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Creating…</> : '+ New'}
            </Button>
          </form>
        </div>
        {error && <div className="text-sm text-destructive mb-3">{error}</div>}

        {/* Saved run configs. Styled as buttons, not tag pills — these navigate,
            unlike the screener tags on the cards below, which are labels. */}
        {wfConfigs.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-5 pb-4 border-b border-border">
            <span className="text-sm font-medium text-foreground shrink-0">Run configs</span>
            <div className="flex flex-wrap gap-2">
              {wfConfigs.map(c => (
                <Button
                  key={c.id}
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs font-normal"
                  onClick={() => navigate(`/wf-configs/${c.id}`)}
                  title={c.run_count ? 'Compare these runs side by side' : 'Not used by any run yet'}
                >
                  <GitCompare className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium">{c.name}</span>
                  <span className="text-muted-foreground">
                    {c.run_count === 0
                      ? 'unused'
                      : `${c.run_count} run${c.run_count === 1 ? '' : 's'} · ${c.helm_count} helm${c.helm_count === 1 ? '' : 's'}`}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <HelmCardSkeleton key={i} />)}
          </div>
        ) : helms.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">No helms yet. Create one above.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {helms.map(h => {
              const screeners = h.screener_names || [];
              const hasStats  = h.last_return_pct != null || h.last_sharpe != null;
              const retVal    = fmt(h.last_return_pct);
              const sharpeVal = fmt(h.last_sharpe, 2);
              const winVal    = fmt(h.last_win_rate_pct);
              const readOnly  = h.is_global && h.is_owner === false && !isSuperadmin;

              return (
                <Card key={h.id} className="flex flex-col hover:border-primary/40 transition-colors">
                  <CardContent className="flex flex-col gap-2 p-4 h-full">

                    {/* Name + description */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-base text-foreground leading-tight">{h.name}</span>
                        {h.is_global && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                            <Globe className="h-2.5 w-2.5" /> Global
                          </span>
                        )}
                      </div>
                      {h.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{h.description}</p>
                      )}
                    </div>

                    {/* Screener tags */}
                    <div className="flex flex-wrap gap-2 min-h-[20px]">
                      {screeners.length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">No screeners</span>
                      ) : (
                        screeners.slice(0, 2).map((s, i) => (
                          <span key={i} className="text-sm bg-muted text-muted-foreground rounded px-1.5 py-0.5">{s}</span>
                        ))
                      )}
                      {screeners.length > 2 && <div>
                        <span data-tooltip-id={`screeners-tooltip-${h.id}`} className='text-xs cursor-pointer text-primary pt-1'>+{screeners.slice(2).length} more</span>
                        <Tooltip id={`screeners-tooltip-${h.id}`} place="left">
                          <ul className="p-2">
                            {screeners.map((s, i) => (
                            <li key={i} className="text-sm ">{s}</li>
                          ))}
                          </ul>
                        </Tooltip>
                        </div>}
                    </div>

                    {/* Last run + stats */}
                    <div className="flex flex-col gap-1 mt-auto pt-1 border-t border-border/50">
                      {h.last_run_at ? (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>Last run:</span>
                          <span className="text-foreground">{fmtDate(h.last_run_at)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">No runs yet</span>
                      )}

                      {hasStats && (
                        <div className="flex gap-3 text-xs">
                          {retVal != null && (
                            <span className={parseFloat(retVal) >= 0 ? 'text-win' : 'text-loss'}>
                              {parseFloat(retVal) >= 0 ? '+' : ''}{retVal}%
                            </span>
                          )}
                          {sharpeVal != null && (
                            <span className="text-muted-foreground">Sharpe <span className="text-foreground">{sharpeVal}</span></span>
                          )}
                          {winVal != null && (
                            <span className="text-muted-foreground">Win <span className="text-foreground">{winVal}%</span></span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className='mt-auto flex gap-2'>
                      <div className="flex gap-1.5 pt-1">
                        {readOnly ? (
                          <>
                            <Button variant="secondary" size="sm" className="h-7 px-2 text-xs flex-1" onClick={() => navigate(`/helms/${h.id}`)}>
                              <Eye className="h-3 w-3 mr-1" />View
                            </Button>
                            <Button size="sm" variant="secondary" className="h-7 px-2 text-xs flex-1"
                              onClick={() => navigate(`/helms/${h.id}/backtest`)}>
                              <BarChart2 className="h-3 w-3 mr-1" /> Backtest
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="secondary" size="sm" className="h-7 px-2 text-xs flex-1" onClick={() => navigate(`/helms/${h.id}`)}>
                              <WrenchIcon className="h-3 w-3 mr-1" /> Configure
                            </Button>
                            <Button size="sm" variant="secondary" className="h-7 px-2 text-xs flex-1"
                              onClick={() => navigate(`/helms/${h.id}/backtest`)}>
                              <BarChart2 className="h-3 w-3 mr-1" /> Backtest
                            </Button>
                          </>
                        )}
                        {isSuperadmin && (
                          <Button
                            size="sm" variant="ghost"
                            className={`h-7 px-2 text-xs ${h.is_global ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
                            title={h.is_global ? 'Remove from global' : 'Make global'}
                            onClick={(e) => { e.stopPropagation(); handleToggleGlobal(h.id); }}
                          >
                            {h.is_global ? <GlobeLock className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                          </Button>
                        )}
                        {h.is_owner !== false && (
                          <Button
                            variant="ghost" size="sm" onClick={() => handleDelete(h.id, h.name)}
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      {confirmDialog}
    </AppLayout>
  );
}
